import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { useAuth } from '../hooks/useAuth'
import { useOrganization } from '../hooks/useOrganization'
import { authApi, organizationApi } from '../services/api'
import { ROLE_HIERARCHY, ROLES } from '../constants/roles'
import {
  Button,
  ChangeSet,
  DataTable,
  DecisionReview,
  FormField,
  FormLayout,
  FormLayoutActions,
  FormLayoutRow,
  FormLayoutSection,
  ModalShell,
  Toggle,
  PageHeader,
  PageLayout,
  StatePanel,
  StatusBadge,
  Tabs,
} from '../components/common'
import { ThemeToggle } from '../components/shell/ThemeToggle'
import { WISettingsSection } from './settings/WISettingsSection'
import styles from './SettingsPage.module.css'

const UPPERCASE_LETTER_PATTERN = /[A-Z]/
const LOWERCASE_LETTER_PATTERN = /[a-z]/
const DECIMAL_DIGIT_PATTERN = /[0-9]/
const SPECIAL_CHARACTER_PATTERN = /[^A-Za-z0-9]/

const defaultPersonalizedBetsSettings = {
  enabled: false,
  provider: 'kambi',
  source_mode: 'gcs',
  operator_codes: [''],
  gcs_bucket: '',
  gcs_prefix: '',
  default_generated_post_creator_id: '',
}

const normalizePersonalizedBetsSettings = (settings = {}) => {
  const operatorCodes = Array.isArray(settings.operator_codes)
    ? settings.operator_codes.map((code) => String(code))
    : []
  return {
    ...defaultPersonalizedBetsSettings,
    ...settings,
    provider: settings.provider || 'kambi',
    source_mode: settings.source_mode || 'gcs',
    operator_codes: operatorCodes.length > 0 ? operatorCodes : [''],
  }
}

let nextOperatorCodeRowKey = 0

const createOperatorCodeRowKey = () => {
  nextOperatorCodeRowKey += 1
  return `operator-code-${nextOperatorCodeRowKey}`
}

const createOperatorCodeRowKeys = (operatorCodes) => operatorCodes.map(() => createOperatorCodeRowKey())

const formatRuntimeTimestamp = (value) => {
  if (!value) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '—'
  return parsed.toLocaleString()
}

const runtimeHealthTone = (health) => {
  if (health === 'healthy') return 'success'
  if (health === 'degraded') return 'warning'
  if (health === 'error') return 'error'
  if (health === 'starting') return 'info'
  return 'default'
}

function SettingsPage() {
  const { user, authConfig, refreshProfile, setupMFA } = useAuth()
  const { currentOrg, isSuperAdmin } = useOrganization()
  // Renaming an organization is internal-only in the API, so the section is too.
  const isInternalAdmin = (ROLE_HIERARCHY[currentOrg?.role] || 0) >= ROLE_HIERARCHY[ROLES.INTERNAL_ORG_ADMIN]

  // User Profile State
  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  
  // Password Change State
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  
  // Organization Settings State
  const [orgName, setOrgName] = useState('')
  const [profileBaseline, setProfileBaseline] = useState('')
  const [organizationBaseline, setOrganizationBaseline] = useState({ name: '' })

  // Personalized Bets State
  const [personalizedBets, setPersonalizedBets] = useState(defaultPersonalizedBetsSettings)
  const [personalizedBetsBaseline, setPersonalizedBetsBaseline] = useState(defaultPersonalizedBetsSettings)
  const [personalizedBetsLoading, setPersonalizedBetsLoading] = useState(false)
  const [personalizedBetsError, setPersonalizedBetsError] = useState(null)
  const [operatorCodeRowKeys, setOperatorCodeRowKeys] = useState(
    () => createOperatorCodeRowKeys(defaultPersonalizedBetsSettings.operator_codes)
  )

  // Source Feed Runtime State
  const [sourceFeedRuntime, setSourceFeedRuntime] = useState([])
  const [sourceFeedRuntimeLoading, setSourceFeedRuntimeLoading] = useState(false)
  const [sourceFeedRuntimeError, setSourceFeedRuntimeError] = useState(null)

  // UI State
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [showMFASetup, setShowMFASetup] = useState(false)
  const [mfaSecret, setMfaSecret] = useState('')
  const [mfaQRCode, setMfaQRCode] = useState('')
  const [mfaCode, setMfaCode] = useState('')
  const [showMFADisableConfirm, setShowMFADisableConfirm] = useState(false)
  const [mfaDisableCode, setMfaDisableCode] = useState('')

  useEffect(() => {
    if (user) {
      setDisplayName(user.display_name || '')
      setProfileBaseline(user.display_name || '')
      setEmail(user.email || '')
    }
    
    if (currentOrg) {
      setOrgName(currentOrg.name || '')
      setOrganizationBaseline({ name: currentOrg.name || '' })
    }
  }, [user, currentOrg])

  const loadPersonalizedBetsSettings = useCallback(async () => {
    if (!currentOrg?.id || !isSuperAdmin) return
    setPersonalizedBetsLoading(true)
    setPersonalizedBetsError(null)
    try {
      const settings = await organizationApi.getPersonalizedBetsSettings(currentOrg.id)
      const normalized = normalizePersonalizedBetsSettings(settings)
      setOperatorCodeRowKeys(createOperatorCodeRowKeys(normalized.operator_codes))
      setPersonalizedBets(normalized)
      setPersonalizedBetsBaseline(normalized)
    } catch (loadError) {
      const message = loadError.response?.data?.error || 'Failed to load personalized bets settings'
      setPersonalizedBetsError(message)
      toast.error(message)
    } finally {
      setPersonalizedBetsLoading(false)
    }
  }, [currentOrg?.id, isSuperAdmin])

  useEffect(() => {
    if (!currentOrg?.id || !isSuperAdmin) {
      setOperatorCodeRowKeys(createOperatorCodeRowKeys(defaultPersonalizedBetsSettings.operator_codes))
      setPersonalizedBets(defaultPersonalizedBetsSettings)
      setPersonalizedBetsBaseline(defaultPersonalizedBetsSettings)
      setPersonalizedBetsError(null)
      return
    }
    loadPersonalizedBetsSettings()
  }, [currentOrg?.id, isSuperAdmin, loadPersonalizedBetsSettings])

  const loadSourceFeedRuntime = useCallback(async () => {
    if (!currentOrg?.id || !isSuperAdmin) return
    setSourceFeedRuntimeLoading(true)
    setSourceFeedRuntimeError(null)
    try {
      const controls = await organizationApi.getSourceFeedRuntime(currentOrg.id)
      setSourceFeedRuntime(Array.isArray(controls) ? controls : [])
    } catch (loadError) {
      setSourceFeedRuntimeError(
        loadError.response?.data?.error || 'Failed to load source feed runtime status'
      )
    } finally {
      setSourceFeedRuntimeLoading(false)
    }
  }, [currentOrg?.id, isSuperAdmin])

  useEffect(() => {
    if (!currentOrg?.id || !isSuperAdmin) {
      setSourceFeedRuntime([])
      setSourceFeedRuntimeError(null)
      return
    }
    loadSourceFeedRuntime()
  }, [currentOrg?.id, isSuperAdmin, loadSourceFeedRuntime])

  const dirtyState = useMemo(() => ({
    profile: displayName !== profileBaseline,
    password: Boolean(currentPassword || newPassword || confirmPassword),
    organization: orgName !== organizationBaseline.name,
    personalizedBets: JSON.stringify(personalizedBets) !== JSON.stringify(personalizedBetsBaseline),
  }), [
    confirmPassword,
    currentPassword,
    displayName,
    newPassword,
    organizationBaseline,
    orgName,
    personalizedBets,
    personalizedBetsBaseline,
    profileBaseline,
  ])

  const hasTooManyGCSOperatorCodes = (
    personalizedBets.source_mode === 'gcs'
    && personalizedBets.operator_codes.length > 1
  )

  const personalizedBetChanges = [
    { label: 'Status', current: personalizedBetsBaseline.enabled ? 'Enabled' : 'Disabled', proposed: personalizedBets.enabled ? 'Enabled' : 'Disabled', impact: personalizedBets.enabled ? 'Generated bet content may be created' : 'Generated bet content stops' },
    { label: 'Source mode', current: personalizedBetsBaseline.source_mode, proposed: personalizedBets.source_mode, impact: 'Changes where source coupons are read' },
    { label: 'Operators', current: personalizedBetsBaseline.operator_codes.filter(Boolean).join(', ') || 'None', proposed: personalizedBets.operator_codes.filter(Boolean).join(', ') || 'None', impact: 'Changes which Kambi streams are routed' },
    { label: 'Generated creator', current: personalizedBetsBaseline.default_generated_post_creator_id || 'System default', proposed: personalizedBets.default_generated_post_creator_id || 'System default', impact: 'Changes ownership of generated posts' },
    ...(personalizedBets.source_mode === 'gcs' ? [
      {
        label: 'GCS bucket',
        current: personalizedBetsBaseline.gcs_bucket || 'Not configured',
        proposed: personalizedBets.gcs_bucket || 'Not configured',
        impact: 'Changes which archive bucket supplies source coupons',
      },
      {
        label: 'GCS prefix',
        current: personalizedBetsBaseline.gcs_prefix || 'Not configured',
        proposed: personalizedBets.gcs_prefix || 'Not configured',
        impact: 'Changes the archive path used to find source coupons',
      },
    ] : []),
  ].filter((item) => item.current !== item.proposed)

  const validatePassword = () => {
    const newErrors = {}

    if (newPassword) {
      if (newPassword.length < 8) {
        newErrors.password = 'Password must be at least 8 characters'
      } else if (!UPPERCASE_LETTER_PATTERN.test(newPassword)) {
        newErrors.password = 'Password must contain an uppercase letter'
      } else if (!LOWERCASE_LETTER_PATTERN.test(newPassword)) {
        newErrors.password = 'Password must contain a lowercase letter'
      } else if (!DECIMAL_DIGIT_PATTERN.test(newPassword)) {
        newErrors.password = 'Password must contain a digit'
      } else if (!SPECIAL_CHARACTER_PATTERN.test(newPassword)) {
        newErrors.password = 'Password must contain a special character'
      }
    }

    if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleUpdateProfile = async () => {
    setLoading(true)

    try {
      await authApi.updateProfile({ display_name: displayName })
      await refreshProfile()
      setProfileBaseline(displayName)
      toast.success('Profile updated successfully')
    } catch {
      toast.error('Failed to update profile')
    } finally {
      setLoading(false)
    }
  }

  const handleChangePassword = async () => {
    if (!currentPassword) {
      setErrors({ currentPassword: 'Current password is required' })
      return
    }
    
    if (!validatePassword()) return
    
    setLoading(true)

    try {
      await authApi.changePassword(currentPassword, newPassword)
      toast.success('Password changed successfully')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setErrors({})
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to change password')
    } finally {
      setLoading(false)
    }
  }

  const handleEnableMFA = async () => {
    setLoading(true)

    try {
      const response = await setupMFA(true)
      setMfaSecret(response.secret)
      setMfaQRCode(response.qr_code)
      setShowMFASetup(true)
    } catch {
      toast.error('Failed to setup MFA')
    } finally {
      setLoading(false)
    }
  }

  const handleDisableMFA = () => {
    setMfaDisableCode('')
    setShowMFADisableConfirm(true)
  }

  const handleConfirmDisableMFA = async () => {
    setLoading(true)

    try {
      await authApi.disableMFA(mfaDisableCode)
      await refreshProfile()
      setShowMFADisableConfirm(false)
      setMfaDisableCode('')
      toast.success('MFA disabled successfully')
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to disable MFA. Check your verification code.')
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyMFA = async () => {
    setLoading(true)
    
    try {
      await authApi.verifyMFA(mfaCode)
      await refreshProfile()
      setShowMFASetup(false)
      setMfaCode('')
      toast.success('MFA enabled successfully')
    } catch {
      toast.error('Invalid verification code')
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateOrganization = async () => {
    setLoading(true)

    try {
      await organizationApi.updateOrganization(currentOrg.id, {
        name: orgName
      })
      setOrganizationBaseline((current) => ({ ...current, name: orgName }))
      toast.success('Organization settings updated successfully')
    } catch {
      toast.error('Failed to update organization settings')
    } finally {
      setLoading(false)
    }
  }

  const handleUpdatePersonalizedBets = async () => {
    setLoading(true)

    const payload = {
      enabled: personalizedBets.enabled,
      provider: 'kambi',
      source_mode: personalizedBets.source_mode,
      operator_codes: personalizedBets.operator_codes.map((code) => code.trim()).filter(Boolean),
      default_generated_post_creator_id: personalizedBets.default_generated_post_creator_id,
    }

    if (personalizedBets.source_mode === 'gcs') {
      payload.gcs_bucket = personalizedBets.gcs_bucket
      payload.gcs_prefix = personalizedBets.gcs_prefix
    }

    try {
      const updated = await organizationApi.updatePersonalizedBetsSettings(currentOrg.id, payload)
      const normalized = normalizePersonalizedBetsSettings(updated)
      setOperatorCodeRowKeys(createOperatorCodeRowKeys(normalized.operator_codes))
      setPersonalizedBets(normalized)
      setPersonalizedBetsBaseline(normalized)
      toast.success('Personalized bets settings updated successfully')
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update personalized bets settings')
    } finally {
      setLoading(false)
    }
  }

  const sourceFeedRuntimeColumns = useMemo(() => [
    {
      key: 'operator_code',
      label: 'Operator',
      sortable: true,
    },
    {
      key: 'offering_ids',
      label: 'Offerings',
      render: (row) => row.offering_ids?.join(', ') || '—',
    },
    {
      key: 'health_status',
      label: 'Health',
      render: (row) => (
        <StatusBadge status={runtimeHealthTone(row.health_status)}>
          {row.health_status || 'unknown'}
        </StatusBadge>
      ),
    },
    {
      key: 'stages',
      label: 'Stages',
      render: (row) => [
        row.planning_enabled ? 'plan on' : 'plan off',
        row.sync_enabled ? 'sync on' : 'sync off',
        row.publication_enabled ? 'publish on' : 'publish off',
      ].join(' · '),
    },
    {
      key: 'last_source_event_at',
      label: 'Last source event',
      render: (row) => formatRuntimeTimestamp(row.last_source_event_at),
    },
    {
      key: 'last_source_offset',
      label: 'Offset',
      numeric: true,
      render: (row) => row.last_source_offset ?? '—',
    },
    {
      key: 'consecutive_failures',
      label: 'Failures',
      numeric: true,
    },
    {
      key: 'latest_run_status',
      label: 'Latest run',
      render: (row) => row.latest_run_status
        ? <StatusBadge auto>{row.latest_run_status}</StatusBadge>
        : '—',
    },
  ], [])

  return (
    <PageLayout size="narrow">
      <PageHeader title="Settings" />

      <Tabs
        label="Settings sections"
        keepMounted
        items={[
          {
            value: 'account',
            label: 'Profile & security',
            panel: (
              <FormLayout>

      <FormLayoutSection
        title="Profile" 
      >
        <form
          className={styles.formStack}
          onSubmit={(event) => {
            event.preventDefault()
            handleUpdateProfile()
          }}
        >
          <FormLayoutRow columns={2}>
            <FormField
              label="Display Name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter your display name"
            />
            <FormField
              label="Email"
              type="email"
              value={email}
              disabled
              help="Email is managed by your identity provider."
            />
          </FormLayoutRow>
          
          <FormLayoutActions>
            <DirtyState dirty={dirtyState.profile} />
            <Button type="submit" disabled={loading || !dirtyState.profile}>
              Save profile
            </Button>
          </FormLayoutActions>
        </form>
      </FormLayoutSection>

      <FormLayoutSection
        title="Appearance"
        description="Choose how Studio looks on your display. System mode matches your operating system."
      >
        <ThemeToggle variant="inline" />
      </FormLayoutSection>

      <FormLayoutSection
        title="Security" 
        description="Keep access secure with a strong password and an authenticator app."
      >
        <div className={styles.subsection}>
          <h3>Password</h3>
          <form
            className={styles.formStack}
            onSubmit={(event) => {
              event.preventDefault()
              handleChangePassword()
            }}
          >
            <input
              className={styles.srOnly}
              type="text"
              name="username"
              aria-label="Username"
              value={email}
              readOnly
              tabIndex={-1}
              autoComplete="username"
            />
            <FormField
              label="Current Password"
              type="password"
              value={currentPassword}
              onChange={(e) => {
                setCurrentPassword(e.target.value)
                if (errors.currentPassword) {
                  setErrors({ ...errors, currentPassword: '' })
                }
              }}
              error={errors.currentPassword}
              autoComplete="current-password"
            />
            
            <FormLayoutRow columns={2}>
              <FormField
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value)
                  if (e.target.value.length >= 8 && errors.password) {
                    setErrors({ ...errors, password: '' })
                  }
                }}
                error={errors.password}
                help="Minimum 8 characters with uppercase, lowercase, number, and symbol."
                autoComplete="new-password"
              />
              <FormField
                label="Confirm New Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value)
                  if (e.target.value === newPassword && errors.confirmPassword) {
                    setErrors({ ...errors, confirmPassword: '' })
                  }
                }}
                error={errors.confirmPassword}
                autoComplete="new-password"
              />
            </FormLayoutRow>
            
            <FormLayoutActions>
              <DirtyState dirty={dirtyState.password} />
              <Button type="submit" disabled={loading || !currentPassword || !newPassword}>
                Change password
              </Button>
            </FormLayoutActions>
          </form>
        </div>

        <div className={styles.subsection}>
          <h3>Two-Factor Authentication</h3>
          <div className={styles.mfaStatus}>
            <span>Status</span>
            <StatusBadge status={user?.mfa_enabled ? 'success' : 'default'}>
              {user?.mfa_enabled ? 'Enabled' : 'Disabled'}
            </StatusBadge>
          </div>
          
          <FormLayoutActions>
            {!user?.mfa_enabled ? (
              <Button
                onClick={handleEnableMFA}
                disabled={loading}
              >
                Enable MFA
              </Button>
            ) : authConfig?.mfa_required ? (
              <p>Two-factor authentication is required for all accounts and cannot be disabled.</p>
            ) : (
              <Button
                onClick={handleDisableMFA}
                disabled={loading}
                variant="danger"
              >
                Disable MFA
              </Button>
            )}
          </FormLayoutActions>
        </div>
      </FormLayoutSection>

              </FormLayout>
            ),
          },
          ...(isInternalAdmin || isSuperAdmin ? [{
            value: 'organization',
            label: 'Organization',
            panel: (
              <FormLayout>

      {isInternalAdmin && (
        <FormLayoutSection
          title="Organization Settings"
        >
          <form
            className={styles.formStack}
            onSubmit={(event) => {
              event.preventDefault()
              handleUpdateOrganization()
            }}
          >
            <FormField
              label="Organization Name"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              placeholder="Enter organization name"
            />
            
            <FormLayoutActions>
              <DirtyState dirty={dirtyState.organization} />
              <Button type="submit" disabled={loading || !dirtyState.organization}>
                Save organization settings
              </Button>
            </FormLayoutActions>
          </form>
        </FormLayoutSection>
      )}


      {isSuperAdmin && (
        <FormLayoutSection
          title="Personalized Bets"
          description="Enable generated Kambi bet content for this organization."
        >
          {personalizedBetsLoading ? (
            <StatePanel state="loading" title="Loading personalized bets" compact />
          ) : personalizedBetsError ? (
            <StatePanel state="error" title="Personalized bets are unavailable" description={personalizedBetsError} onRetry={loadPersonalizedBetsSettings} compact />
          ) : (
          <form
            className={styles.formStack}
            onSubmit={(event) => {
              event.preventDefault()
              handleUpdatePersonalizedBets()
            }}
          >
            <Toggle
              label="Personalized bets"
              checked={personalizedBets.enabled}
              onChange={(enabled) => setPersonalizedBets((current) => ({ ...current, enabled }))}
              disabled={personalizedBetsLoading}
            />

            <FormLayoutRow columns={2}>
              <FormField
                label="Provider"
                value="Kambi"
                disabled
                help="Only Kambi coupon sources are supported for this feature."
              />
              <FormField
                label="Source mode"
                type="select"
                value={personalizedBets.source_mode}
                onChange={(event) => setPersonalizedBets((current) => ({ ...current, source_mode: event.target.value }))}
                options={[
                  { value: 'gcs', label: 'GCS archive' },
                  { value: 'live', label: 'Live PASTA API' },
                ]}
                disabled={personalizedBetsLoading}
              />
            </FormLayoutRow>

            <fieldset className={styles.operatorCodes}>
              <legend>Kambi operator codes</legend>
              <div className={styles.operatorCodesHeader}>
                <p>
                  {hasTooManyGCSOperatorCodes
                    ? 'GCS archive mode supports one Kambi operator code. Remove extra codes before saving.'
                    : personalizedBets.source_mode === 'gcs'
                      ? 'GCS archive mode supports one Kambi operator code.'
                      : 'Route every Kambi stream that supplies personalized bets for this organization.'}
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setOperatorCodeRowKeys((current) => [
                      ...current,
                      createOperatorCodeRowKey(),
                    ])
                    setPersonalizedBets((current) => ({
                      ...current,
                      operator_codes: [...current.operator_codes, ''],
                    }))
                  }}
                  disabled={
                    personalizedBetsLoading
                    || (personalizedBets.source_mode === 'gcs' && personalizedBets.operator_codes.length >= 1)
                  }
                >
                  Add operator
                </Button>
              </div>
              <div className={styles.operatorCodesList}>
                {personalizedBets.operator_codes.map((operatorCode, index) => (
                  <div className={styles.operatorCodeRow} key={operatorCodeRowKeys[index]}>
                    <FormField
                      label={`Operator ${index + 1}`}
                      value={operatorCode}
                      onChange={(event) => setPersonalizedBets((current) => ({
                        ...current,
                        operator_codes: current.operator_codes.map((code, codeIndex) => (
                          codeIndex === index ? event.target.value : code
                        )),
                      }))}
                      placeholder="DESERT_DIAMOND"
                      disabled={personalizedBetsLoading}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label={`Remove operator ${index + 1}`}
                      onClick={() => {
                        setOperatorCodeRowKeys((current) => current.filter((
                          _,
                          keyIndex
                        ) => keyIndex !== index))
                        setPersonalizedBets((current) => ({
                          ...current,
                          operator_codes: current.operator_codes.filter((
                            _,
                            codeIndex
                          ) => codeIndex !== index),
                        }))
                      }}
                      disabled={personalizedBetsLoading || personalizedBets.operator_codes.length === 1}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            </fieldset>

            {personalizedBets.source_mode === 'gcs' && (
              <FormLayoutRow columns={2}>
                <FormField
                  label="GCS bucket"
                  value={personalizedBets.gcs_bucket}
                  onChange={(event) => setPersonalizedBets((current) => ({ ...current, gcs_bucket: event.target.value }))}
                  placeholder="wager-etl"
                  disabled={personalizedBetsLoading}
                />
                <FormField
                  label="GCS prefix"
                  value={personalizedBets.gcs_prefix}
                  onChange={(event) => setPersonalizedBets((current) => ({ ...current, gcs_prefix: event.target.value }))}
                  placeholder="pasta/production/desert_diamond/"
                  disabled={personalizedBetsLoading}
                />
              </FormLayoutRow>
            )}

            <FormField
              label="Default generated creator ID"
              value={personalizedBets.default_generated_post_creator_id}
              onChange={(event) => setPersonalizedBets((current) => ({ ...current, default_generated_post_creator_id: event.target.value }))}
              placeholder="aaaaaaaa-bbbb-cccc-dddd-eeeeeeeeeeee"
              help="Use the Personalized Bets system creator unless a linked org-specific generated creator should own these posts."
              disabled={personalizedBetsLoading}
            />

            <ChangeSet
              items={personalizedBetChanges}
              title="Pending personalized-bet changes"
              description="Review source routing and generated-content impact before saving."
            />

            <FormLayoutActions>
              <DirtyState dirty={dirtyState.personalizedBets} />
              <Button
                type="submit"
                disabled={loading || !dirtyState.personalizedBets || hasTooManyGCSOperatorCodes}
              >
                Save personalized bets
              </Button>
            </FormLayoutActions>
          </form>
          )}
        </FormLayoutSection>
      )}

      {isSuperAdmin && <WISettingsSection key={currentOrg?.id} orgId={currentOrg?.id} />}

      {isSuperAdmin && (
        <FormLayoutSection
          title="Source feed runtime"
          description="Read-only per-operator health. Source activation and publication remain gate-controlled."
        >
          {sourceFeedRuntimeError ? (
            <StatePanel
              state="error"
              title="Source feed runtime is unavailable"
              description={sourceFeedRuntimeError}
              onRetry={loadSourceFeedRuntime}
              compact
            />
          ) : (
            <DataTable
              label="Source feed runtime controls"
              data={sourceFeedRuntime}
              columns={sourceFeedRuntimeColumns}
              keyField="operator_code"
              density="compact"
              loading={sourceFeedRuntimeLoading}
              loadingMessage="Loading source feed runtime"
              emptyMessage="No source feed runtime controls are configured for this organization."
              paginate
              pageSize={10}
            />
          )}
        </FormLayoutSection>
      )}

              </FormLayout>
            ),
          }] : []),
        ]}
      />

      <ModalShell
        open={showMFASetup}
        title="Set up two-factor authentication"
        description="Scan the QR code with your authenticator app, then confirm the current six-digit code."
        onClose={() => setShowMFASetup(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowMFASetup(false)}>
              Cancel
            </Button>
            <Button onClick={handleVerifyMFA} disabled={loading || !mfaCode}>
              Verify and Enable
            </Button>
          </>
        }
      >
        <div className={styles.modalBody}>
          <div className={styles.qrCode}>
            {mfaQRCode ? <img src={mfaQRCode} alt="MFA QR code" /> : null}
          </div>
          <code className={styles.secretCode}>{mfaSecret}</code>
          <FormField
            label="Verification Code"
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value)}
            placeholder="000000"
            help="Enter the six-digit code from your authenticator app."
          />
        </div>
      </ModalShell>

      <ModalShell
        open={showMFADisableConfirm}
        title="Disable two-factor authentication"
        description="Enter the current authenticator code to confirm that MFA should be disabled."
        onClose={() => setShowMFADisableConfirm(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowMFADisableConfirm(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirmDisableMFA}
              disabled={loading || !mfaDisableCode}
            >
              Disable MFA
            </Button>
          </>
        }
      >
        <div className={styles.modalBody}>
          <DecisionReview
            title="Review security change"
            contextItems={[
              { label: 'Account', value: email || 'Current account' },
              { label: 'Current protection', value: 'Password and authenticator code' },
            ]}
            changeItems={[
              { label: 'Two-factor authentication', current: 'Enabled', proposed: 'Disabled', impact: 'Future sign-ins will require only the primary identity-provider challenge' },
            ]}
            consequence="Disabling MFA removes the authenticator requirement immediately after verification."
          />
          <FormField
            label="Verification Code"
            value={mfaDisableCode}
            onChange={(e) => setMfaDisableCode(e.target.value)}
            placeholder="000000"
            help="Enter the six-digit code from your authenticator app."
          />
        </div>
      </ModalShell>

    </PageLayout>
  )
}

function DirtyState({ dirty }) {
  return (
    <span className={dirty ? styles.dirtyState : styles.savedState} role="status">
      {dirty ? 'Unsaved changes' : 'No pending changes'}
    </span>
  )
}

export default SettingsPage
